import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatDatepickerModule, DateRange } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { ReplayFilterParams } from '../../../services/replay.service';
import { TourAnchorDirective } from '../../../features/learning-center/engine/tour-anchor.directive';

const LS_PRESET  = 'replay_preset_index';
const LS_SOURCE  = 'replay_source_index';
const LS_START   = 'replay_range_start_ms';
const LS_END     = 'replay_range_end_ms';

interface PresetOption {
  label: string;
  days: number;
}

const PRESETS: PresetOption[] = [
  { label: 'Last Day',   days: 1  },
  { label: 'Last Week',  days: 7  },
  { label: 'Last Month', days: 30 },
];

const SOURCE_OPTIONS = ['All Trips', 'My Trips', 'Shared with Me'];

@Component({
  selector: 'app-replay-filter-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDatepickerModule, MatNativeDateModule, TourAnchorDirective],
  templateUrl: './replay-filter-modal.component.html',
  styleUrl: './replay-filter-modal.component.scss',
})
export class ReplayFilterModalComponent implements OnInit {
  /** Active tab: 0 = Timeline, 1 = View */
  activeTab = 0;

  /** -1 = custom range selected */
  presetIndex = 2;
  sourceIndex = 0;

  customStart: Date | null = null;
  customEnd: Date | null = null;

  saveFilters = true;

  /** Validation error for date range > 31 days */
  dateRangeError = '';

  readonly presets = PRESETS;
  readonly sourceOptions = SOURCE_OPTIONS;

  // ── Calendar range picker (mirrors Map Display Filter) ──────────────────────
  showCalendar = false;
  minDate = new Date(2020, 0, 1);
  maxDate = new Date();
  selectedRange = new DateRange<Date>(null, null);

  constructor(private dialogRef: MatDialogRef<ReplayFilterModalComponent>) {}

  ngOnInit(): void {
    this._loadFromStorage();
  }

  private _loadFromStorage(): void {
    try {
      const preset = parseInt(localStorage.getItem(LS_PRESET) ?? '2', 10);
      const source = parseInt(localStorage.getItem(LS_SOURCE) ?? '0', 10);
      this.presetIndex = isNaN(preset) ? 2 : Math.max(-1, Math.min(2, preset));
      this.sourceIndex = isNaN(source) ? 0 : Math.max(0, Math.min(2, source));
      if (this.presetIndex === -1) {
        const startMs = parseInt(localStorage.getItem(LS_START) ?? '', 10);
        const endMs   = parseInt(localStorage.getItem(LS_END) ?? '', 10);
        if (!isNaN(startMs) && !isNaN(endMs)) {
          this.customStart = new Date(startMs);
          this.customEnd   = new Date(endMs);
          this.selectedRange = new DateRange<Date>(this.customStart, this.customEnd);
        } else {
          this.presetIndex = 2; // fallback to Last Month
        }
      }
    } catch { /* ignore storage errors */ }
  }

  private _saveToStorage(): void {
    if (!this.saveFilters) return;
    try {
      localStorage.setItem(LS_PRESET, String(this.presetIndex));
      localStorage.setItem(LS_SOURCE, String(this.sourceIndex));
      if (this.presetIndex === -1 && this.customStart && this.customEnd) {
        localStorage.setItem(LS_START, String(this.customStart.getTime()));
        localStorage.setItem(LS_END,   String(this.customEnd.getTime()));
      } else {
        localStorage.removeItem(LS_START);
        localStorage.removeItem(LS_END);
      }
    } catch { /* ignore */ }
  }

  selectPreset(index: number): void {
    this.presetIndex = index;
    this.customStart = null;
    this.customEnd = null;
    this.selectedRange = new DateRange<Date>(null, null);
    this.dateRangeError = '';
  }

  selectSource(index: number): void {
    this.sourceIndex = index;
  }

  /** Tapping the Custom Range row selects it and opens the full-screen calendar. */
  openCustomRange(): void {
    this.presetIndex = -1;
    if (!this.customStart || !this.customEnd) {
      const now = new Date();
      const weekAgo = new Date(now.getTime() - 7 * 86_400_000);
      this.customStart = weekAgo;
      this.customEnd = now;
      this.selectedRange = new DateRange<Date>(weekAgo, now);
    }
    this.dateRangeError = '';
    this.showCalendar = true;
  }

  /** mat-calendar range selection — first tap sets start, second sets end. */
  onDateSelected(date: Date): void {
    const cur = this.selectedRange;
    let next: DateRange<Date>;
    if (!cur.start || cur.end) {
      next = new DateRange<Date>(date, null);
    } else {
      next = date < cur.start ? new DateRange<Date>(date, cur.start) : new DateRange<Date>(cur.start, date);
    }
    this.selectedRange = next;
    // Sync to the Date pair used for results: start at 00:00, end at 23:59:59.
    this.customStart = next.start ? this._atStartOfDay(next.start) : null;
    this.customEnd   = next.end   ? this._atEndOfDay(next.end)     : null;
    this._validateCustomRange();
  }

  closeCalendar(): void { this.showCalendar = false; }
  saveCalendar(): void { this.showCalendar = false; }

  /** Long range label shown above the calendar (e.g. "March 3 – March 9"). */
  getFullDateRange(): string {
    const s = this.selectedRange.start;
    const e = this.selectedRange.end;
    const opts: Intl.DateTimeFormatOptions = { month: 'long', day: 'numeric' };
    if (!s) return 'Select date range';
    const ss = s.toLocaleDateString('en-US', opts);
    if (!e || s.getTime() === e.getTime()) return ss;
    return `${ss} – ${e.toLocaleDateString('en-US', opts)}`;
  }

  private _validateCustomRange(): void {
    if (!this.customStart || !this.customEnd) { this.dateRangeError = ''; return; }
    const diff = (this.customEnd.getTime() - this.customStart.getTime()) / 86_400_000;
    if (diff > 31) {
      this.dateRangeError = 'Cannot select a date range longer than 1 month.';
    } else if (diff < 0) {
      this.dateRangeError = 'End date must be after start date.';
    } else {
      this.dateRangeError = '';
    }
  }

  get customRangeLabel(): string {
    if (!this.customStart || !this.customEnd) return 'Pick a date range';
    return `${this._fmtDate(this.customStart)} – ${this._fmtDate(this.customEnd)}`;
  }

  get canStart(): boolean {
    if (this.presetIndex === -1) {
      return !!this.customStart && !!this.customEnd && !this.dateRangeError;
    }
    return true;
  }

  start(): void {
    if (!this.canStart) return;
    this._saveToStorage();
    const showMine   = this.sourceIndex === 0 || this.sourceIndex === 1;
    const showShared = this.sourceIndex === 0 || this.sourceIndex === 2;

    let result: ReplayFilterParams;
    if (this.presetIndex === -1 && this.customStart && this.customEnd) {
      result = { showMine, showShared, customRange: { start: this.customStart, end: this.customEnd } };
    } else {
      const days = PRESETS[this.presetIndex]?.days ?? 30;
      result = { showMine, showShared, historyDays: days };
    }
    this.dialogRef.close(result);
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  private _atStartOfDay(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0);
  }

  private _atEndOfDay(d: Date): Date {
    return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59);
  }

  private _fmtDate(d: Date): string {
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }
}
